import { useState, useEffect } from "react";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader/Loader";
import CustomToolbar from "../../../../components/toolbar";
import { SuperPowerServices } from "../../../../services/challenge_services"; // Adjust the path as needed
import { Breadcrumbs, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import classes from "../../Challenge.module.css"; // Adjust the path as needed
import AUTH_CONTEXT from "../../../../store";
import { BASE_PATH_TO_ADD_SUPER_POWERS, BASE_PATH_TO_UPDATE_SUPER_POWERS } from "../../../../base_routes_paths/challenge_paths";

const SuperPowersList = () => {
    const navigate = useNavigate();
    const { token, fileName } = AUTH_CONTEXT();
    const EXPORT_FILENAME = `${fileName}_SUPERPOWERS`;

    const columns = [
        {
            field: "SuperPowerName",
            headerName: "SuperPower Name",
            width: 400,
            renderCell: (params) => {
                const SuperPowerName = params.row.SuperPowerName;
                return (
                    <table style={{ textAlign: "center" }}>
                        <thead>
                            <tr>
                                <th style={{ paddingRight: 25 }}>Locale</th>
                                <th style={{ paddingRight: 25 }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(SuperPowerName).map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ paddingRight: 25 }}>{key}</td>
                                    <td style={{ paddingRight: 25 }}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            field: "SuperPowerDescription",
            headerName: "SuperPower Description",
            width: 400,
            renderCell: (params) => {
                const SuperPowerDescription = params.row.SuperPowerDescription;
                return (
                    <table style={{ textAlign: "center" }}>
                        <thead>
                            <tr>
                                <th style={{ paddingRight: 25 }}>Locale</th>
                                <th style={{ paddingRight: 25 }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(SuperPowerDescription).map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ paddingRight: 25 }}>{key}</td>
                                    <td style={{ paddingRight: 25 }}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            field: "SuperPowerImageUrl",
            headerName: "Image URL",
            width: 300,
            renderCell: (params) => {
                const link = params.row.SuperPowerImageUrl;
                return (
                    <>
                        <ContentCopyIcon
                            sx={{ marginRight: "10px", cursor: "pointer" }}
                            onClick={() => {
                                navigator.clipboard.writeText(link);
                                toast.success("Copied Link!");
                            }}
                        />
                        <a href={link} target="_blank" rel="noopener noreferrer">
                            {link}
                        </a>
                    </>
                );
            },
        },
        { field: "SuperPowerDuration", headerName: "Duration (Days)", width: 150 },
        { field: "SuperPowerDurationType", headerName: "Duration Type", width: 150 },
        { field: "SuperPowerMultiplier", headerName: "Multiplier", width: 100 },
        { field: "SuperPowerPointsCost", headerName: "Points Cost", width: 120 },
        { field: "SuperPowerCategory", headerName: "Category", width: 150 },
        { field: "IsActive", headerName: "Active", width: 100 },
    ];

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);

    useEffect(() => {
        const fetchResponse = async () => {
            setIsLoading(true);
            try {
                const response = await SuperPowerServices.getSuperPowers(token, navigate, null);
                const formattedData = response.data.map(({ _id: id, ...rest }) => ({ id, ...rest }));
                setData(formattedData);
                toast.success("Data fetched successfully!", { position: "bottom-left", autoClose: 2000 });
            } catch (error) {
                toast.error("Failed to fetch data: " + error.message, { position: "bottom-left", autoClose: 5000 });
            } finally {
                setIsLoading(false);
            }
        };
        fetchResponse();
    }, []);

    const handleDelete = (id) => {
        SuperPowerServices.deleteSuperPowers(id, token);
    };

    const actionColumn = [
        {
            field: "action",
            headerName: "Action",
            width: 200,
            renderCell: (params) => {
                return (
                    <div className={classes.cellAction}>
                        <Link
                            href={`${BASE_PATH_TO_UPDATE_SUPER_POWERS}/` + params.row.id}
                            style={{ textDecoration: "none" }}
                        >
                            <div className={classes.viewButton}>Update</div>
                        </Link>
                        <div
                            className={classes.deleteButton}
                            onClick={() => handleDelete(params.row.id)}
                        >
                            Delete
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className={classes.datatable}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 1 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                    Dashboard
                </Link>
                <Typography color="text.primary">Super Powers List</Typography>
            </Breadcrumbs>
            {isLoading ? (
                <Loader />
            ) : (
                <DataGridPremium
                    rows={data}
                    getRowId={(row) => row.id}
                    columns={columns.concat(actionColumn)}
                    slots={{
                        toolbar: () => (
                            <CustomToolbar
                                EXPORT_FILENAME={EXPORT_FILENAME}
                                onClickCreate={() => {
                                    navigate(BASE_PATH_TO_ADD_SUPER_POWERS);
                                }}
                                displayCreate={""}
                                onClickUpdate={() => { }}
                                displayUpdate={"none"}
                            />
                        ),
                    }}
                    pagination
                    rowHeight={150}
                    disableRowSelectionOnClick
                    checkboxSelection
                />
            )}
        </div>
    );
};

export default SuperPowersList;
